/**
 * BaselineExportTab Component
 *
 * Downloads the tenant's baseline configuration report in CSV or HTML. Part
 * of the Configuration Drift hub; extracted from pages/BaselineReports.tsx
 * into a tab component.
 *
 * Carbon: one tile with the format as selectable tiles (RadioGroup) and the
 * download as the view's primary button.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import type { Tenant } from '@/types'
import EmptyState from '@/components/shared/EmptyState'
import { Button } from '@/components/carbon/Button'
import { IconDownload } from '@/components/carbon/icons'
import { RadioGroup } from '@/components/carbon/RadioGroup'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'

export interface BaselineExportTabProps {
  tenantId: string | undefined
}

const FORMATS = [
  { value: 'csv', label: 'CSV', helper: 'Opens in Excel or any spreadsheet.' },
  { value: 'html', label: 'HTML', helper: 'Formatted for auditors.' },
]

export default function BaselineExportTab({ tenantId }: BaselineExportTabProps) {
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'html'>('csv')
  const [isDownloading, setIsDownloading] = useState(false)

  // Use cached tenants query to resolve tenant name for the download filename
  const { data: tenants = [] } = useQuery<Tenant[]>({
    queryKey: ['tenants'],
    queryFn: api.getTenants,
    staleTime: 5 * 60 * 1000,
  })

  const handleDownload = async () => {
    if (!tenantId) {
      toast.error('Select a tenant first.')
      return
    }

    setIsDownloading(true)

    try {
      const blob = await api.downloadBaselineReport(tenantId, selectedFormat)

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const tenant = tenants.find((t) => t.tenantId === tenantId)
      const tenantName = tenant?.name?.replace(/[^a-zA-Z0-9]/g, '-') || tenantId
      const timestamp = new Date().toISOString().split('T')[0]
      link.download = `baseline-report-${tenantName}-${timestamp}.${selectedFormat}`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success(`${selectedFormat.toUpperCase()} report downloaded.`)
    } catch (err: any) {
      console.error('Download error:', err)
      if (err.response?.status === 404) {
        toast.error('No baseline on record for this tenant. Run a scan to create one.')
      } else if (err.response?.status === 403) {
        toast.error('You do not have access to this tenant.')
      } else {
        toast.error('Report download failed. Try again.')
      }
    } finally {
      setIsDownloading(false)
    }
  }

  // No tenant selected state
  if (!tenantId) {
    return (
      <Tile padding={24}>
        <EmptyState headline="Select a tenant to download its baseline report." align="center" />
      </Tile>
    )
  }

  return (
    <Tile as="section" padding={24} aria-labelledby="baseline-export-title" className="flex flex-col gap-6">
      <div>
        <TileHeader id="baseline-export-title" title="Baseline configuration report" />
        <p className="m-0 mt-2 max-w-[56ch] text-text-secondary">
          Guardian exports the Microsoft 365 configuration it holds, dated to the capture.
        </p>
      </div>

      <RadioGroup
        name="baseline-report-format"
        legend="Report format"
        variant="tile"
        value={selectedFormat}
        onChange={(value) => setSelectedFormat(value as 'csv' | 'html')}
        options={FORMATS}
        disabled={isDownloading}
        className="max-w-xl"
      />

      <div>
        <Button kind="primary" onClick={() => void handleDownload()} loading={isDownloading} icon={<IconDownload />}>
          {isDownloading ? 'Generating report' : `Download ${selectedFormat.toUpperCase()} report`}
        </Button>
        <p className="m-0 mt-4 text-helper text-text-helper">
          Use the report as auditor documentation or for compliance verification.
        </p>
      </div>
    </Tile>
  )
}
